import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, LogOut, UserRound, Upload } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { FileImage } from "@/components/FileImage";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROWS = [
  ["name", "Full name"],
  ["email", "Email ID"],
  ["phone", "Phone number"],
  ["address", "Address"],
  ["emergency_contact_name", "Emergency contact name"],
  ["emergency_contact_phone", "Emergency contact number"],
  ["vet_name", "Vet name"],
  ["vet_phone", "Vet phone number"],
];

const ID_STATUS = {
  verified: { label: "Verified", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Couldn't be verified", cls: "bg-red-100 text-red-600" },
  pending: { label: "Pending review", cls: "bg-amberwarm/15 text-amberwarm" },
};

const Profile = () => {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);

  const initials = (user?.name || "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const start = () => {
    setForm({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      address: user.address || "",
      emergency_contact_name: user.emergency_contact_name || "",
      emergency_contact_phone: user.emergency_contact_phone || "",
      vet_name: user.vet_name || "",
      vet_phone: user.vet_phone || "",
    });
    setEdit(true);
  };

  const save = async () => {
    if (!form.name?.trim() || !form.email?.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.put("/users/me", form);
      setUser(r.data);
      setEdit(false);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const uploadId = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/photo-id", fd);
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success("Photo ID uploaded — pending verification");
      e.target.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingId(false);
    }
  };

  return (
    <section data-testid="profile-page" className="pt-28 pb-28 lg:pt-36">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <Reveal y={14}>
          <button
            type="button"
            data-testid="profile-back-button"
            onClick={() => navigate("/account")}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-navy"
          >
            <ArrowLeft className="h-4 w-4 text-bark transition-transform duration-200 group-hover:-translate-x-1" aria-hidden="true" />
            Back to my account
          </button>

          <div className="mt-6 flex items-center gap-4">
            <span className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-navy text-lg font-bold text-cream">
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-full w-full object-cover" />
              ) : initials ? (
                initials
              ) : (
                <UserRound className="h-7 w-7" aria-hidden="true" />
              )}
            </span>
            <div>
              <h1 className="font-display text-3xl font-medium tracking-tight text-ink">{user.name || "Your profile"}</h1>
              <p className="text-sm text-slate-500">{user.email}</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div data-testid="profile-section" className="mt-8 rounded-[2rem] border border-ink/8 bg-white p-7 sm:p-8">
            <div className="flex items-center justify-between gap-4">
              <h2 className="font-display text-xl font-medium tracking-tight text-ink">Contact details</h2>
              {edit ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="profile-cancel"
                    onClick={() => setEdit(false)}
                    className="rounded-full border border-ink/15 px-5 py-2 text-xs font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    data-testid="profile-save"
                    onClick={save}
                    disabled={busy}
                    className="rounded-full bg-bark px-5 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-60"
                  >
                    {busy ? "Saving…" : "Save"}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="profile-edit"
                  onClick={start}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 px-4 py-2 text-xs font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
                >
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  Edit
                </button>
              )}
            </div>
            <div className="mt-6 grid gap-x-8 gap-y-5 sm:grid-cols-2">
              {ROWS.map(([key, label]) => (
                <div key={key}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
                  {edit ? (
                    <input
                      data-testid={`profile-input-${key}`}
                      type={key === "email" ? "email" : key.includes("phone") ? "tel" : "text"}
                      value={form[key] || ""}
                      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                      className="mt-1.5 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-2.5 text-sm text-ink focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25"
                    />
                  ) : (
                    <p data-testid={`profile-value-${key}`} className="mt-1 text-sm font-medium text-ink">
                      {user[key] || "—"}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div data-testid="profile-photo-id-section" className="mt-6 rounded-[2rem] border border-ink/8 bg-white p-7 sm:p-8">
            <div className="flex items-center justify-between gap-4">
              <h2 className="font-display text-xl font-medium tracking-tight text-ink">Photo ID</h2>
              <label
                data-testid="profile-id-upload-label"
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-ink/10 px-4 py-2 text-xs font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
              >
                <Upload className="h-3.5 w-3.5" aria-hidden="true" />
                {uploadingId ? "Uploading…" : user.photo_id ? "Replace ID" : "Upload ID"}
                <input data-testid="profile-id-upload-input" type="file" accept="image/*,.pdf" className="hidden" onChange={uploadId} disabled={uploadingId} />
              </label>
            </div>
            {user.photo_id ? (
              <div data-testid="profile-id-current" className="mt-5 flex flex-wrap items-center gap-4 rounded-2xl bg-cream-dark/50 p-4">
                <a
                  href={`${API}/files/${user.photo_id.storage_path}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="profile-id-view"
                  className="shrink-0"
                >
                  {user.photo_id.content_type?.startsWith("image/") ? (
                    <FileImage path={user.photo_id.storage_path} alt="Photo ID" className="h-20 w-20 rounded-xl object-cover ring-1 ring-ink/10" />
                  ) : (
                    <div className="flex h-20 w-20 items-center justify-center rounded-xl bg-white text-[10px] font-bold text-slate-500 ring-1 ring-ink/10">View PDF</div>
                  )}
                </a>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">{user.photo_id.original_filename || "Your photo ID"}</p>
                  <span className={`mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${(ID_STATUS[user.id_verified] || ID_STATUS.pending).cls}`}>
                    {(ID_STATUS[user.id_verified] || ID_STATUS.pending).label}
                  </span>
                  <a
                    href={`${API}/files/${user.photo_id.storage_path}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 block text-xs font-semibold text-bark hover:underline"
                  >
                    Open / view current ID →
                  </a>
                </div>
              </div>
            ) : (
              <p data-testid="profile-id-empty" className="mt-4 rounded-2xl bg-amberwarm/10 px-4 py-3 text-sm text-slate-600">
                No photo ID on file yet. Upload any government ID (Aadhaar, PAN, Passport or Driving Licence) — it's required before booking.
              </p>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <button
            type="button"
            data-testid="profile-logout-button"
            onClick={handleLogout}
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-navy/15 px-6 py-3 text-sm font-semibold text-navy transition-colors duration-200 hover:border-red-300 hover:text-red-500"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Log out
          </button>
        </Reveal>
      </div>
    </section>
  );
};

export default Profile;
